// client/src/components/media/PostsList.jsx
// List view of all media from Timeline event with "New Post" button

import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import AppFooter from "../AppFooter";
import MediaViewer from "./MediaViewer";
import { FaArrowLeft, FaPaperPlane, FaHome } from "react-icons/fa";
import { sessionRequestConfig } from "../../services/sessionLocal";
import SearchFilterBar from "../shared/SearchFilterBar";

// Helpers
const toAbs = (baseUrl, p) =>
  !p ? p : /^https?:\/\//i.test(p) ? p : `${baseUrl}${p.startsWith('/') ? '' : '/'}${p}`;


const isVideo = (f) =>
  (f?.mimetype?.startsWith?.("video/")) || /\.(mp4|mov|webm|ogg|avi|mkv)$/i.test(f?.path || f || "");

const PostsList = () => {
  const { tenant } = useParams();
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_BASE_URL || (typeof window !== "undefined" ? window.location.origin : "http://localhost:5000");
  const cfg = sessionRequestConfig();

  const [, setTimelineEvent] = useState(null);
  const [allMedia, setAllMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewer, setViewer] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Load Timeline event and its media
  useEffect(() => {
    if (!tenant) return;
    let alive = true;

    (async () => {
      try {
        // Get Timeline event
        const { data: timelineData } = await axios.get(
          `/api/events/${tenant}/timeline/ensure`,
          cfg
        );
        if (!alive) return;

        const evt = timelineData?.event;
        if (!evt?.id) throw new Error("Failed to get Timeline event");

        setTimelineEvent(evt);

        // Fetch all media from Timeline event
        const { data: mediaData } = await axios.get(
          `/api/events/${tenant}/events/${evt.id}/images`,
          cfg
        );
        if (!alive) return;

        const media = mediaData?.images || [];
        setAllMedia(media);

      } catch (err) {
        console.error("Error loading posts:", err);
        if (alive) setError(err?.response?.data?.message || err?.message || "Failed to load posts");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => { alive = false; };
  }, [tenant]); // eslint-disable-line

  // Filter media by search term and date range
  const filteredMedia = useMemo(() => {
    let result = allMedia;
    const q = searchTerm.trim().toLowerCase();
    if (q) {
      result = result.filter((media) => {
        const path = (media?.path || media?.url || "").toLowerCase();
        const title = (media?.title || media?.name || "").toLowerCase();
        const filename = path.split("/").pop() || "";
        return filename.includes(q) || title.includes(q) || path.includes(q);
      });
    }
    if (dateFrom) {
      const from = new Date(dateFrom);
      result = result.filter((media) => {
        const d = media?.createdOn || media?.created_at || media?.uploadedAt;
        return d ? new Date(d) >= from : true;
      });
    }
    if (dateTo) {
      const to = new Date(dateTo + "T23:59:59");
      result = result.filter((media) => {
        const d = media?.createdOn || media?.created_at || media?.uploadedAt;
        return d ? new Date(d) <= to : true;
      });
    }
    // Sort by date descending
    result.sort((a, b) => {
      const da = new Date(a?.createdOn || a?.created_at || a?.uploadedAt || 0);
      const db = new Date(b?.createdOn || b?.created_at || b?.uploadedAt || 0);
      return db - da;
    });
    return result;
  }, [allMedia, searchTerm, dateFrom, dateTo]);

  // Group media by date (like EventHistory)
  const groupedMedia = useMemo(() => {
    const byDate = {};
    for (const media of filteredMedia) {
      const dateStr = new Date().toISOString().slice(0, 10);
      (byDate[dateStr] ||= []).push(media);
    }
    return Object.entries(byDate).sort(([a], [b]) => (a < b ? 1 : -1));
  }, [filteredMedia]);

  const handleNewPost = () => {
    navigate(`/${tenant}/post/new`);
  };

  const footerButtons = [
    {
      id: "back",
      icon: <FaArrowLeft />,
      label: "Back",
      onClick: () => navigate(-1),
      disabled: loading,
    },
    {
      id: "home",
      icon: <FaHome />,
      label: "Home",
      onClick: () => navigate(`/${tenant}/dashboard`),
      disabled: loading,
    },
    {
      id: "new",
      icon: <FaPaperPlane />,
      label: "New Post",
      onClick: handleNewPost,
      disabled: loading,
    },
  ];

  if (loading) {
    return (
      <div className="container">
        <h2 className="hdr hdr--primary hdr--lg">Posts</h2>
        <div>Loading posts...</div>
        <AppFooter buttons={footerButtons} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <h2 className="hdr hdr--primary hdr--lg">Posts</h2>
        <div className="alert alert-danger">{error}</div>
        <AppFooter buttons={footerButtons} />
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="flex items-center justify-between mb-3">
        <h3 className="m-0">Posts</h3>
        <div className="text-gray-400">{allMedia.length} media items</div>
      </div>

      <SearchFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        onClear={() => { setSearchTerm(""); setDateFrom(""); setDateTo(""); }}
        searchPlaceholder="Search posts\u2026"
      />

      {/* Desktop inline action bar (replaces footer on desktop) */}
      <div className="desktop-action-bar">
        <button type="button" onClick={() => navigate(-1)}>
          <FaArrowLeft /> Back
        </button>
        <button type="button" onClick={() => navigate(`/${tenant}/dashboard`)}>
          <FaHome /> Home
        </button>
        <button type="button" className="btn-primary-action" onClick={handleNewPost}>
          <FaPaperPlane /> New Post
        </button>
      </div>

      {filteredMedia.length === 0 ? (
        <div className="alert alert-info">
          No posts yet. Tap "New Post" to create your first post.
        </div>
      ) : (
        groupedMedia.map(([date, mediaArr]) => (
          <div key={date} className="mb-4">
            {/* <h5 className="mb-3">{date}</h5> */}
            <div className="row gap-3" style={{ alignItems: 'stretch' }}>
              {mediaArr.map((media, idx) => {
                const url = toAbs(baseUrl, media.path || media.url || media);
                const isVid = isVideo(media);

                return (
                  <div key={idx} className="col-12 col-sm-6 col-md-4 col-lg-3">
                    <div className="card h-full shadow-sm">
                      <div
                        className="ratio ratio-4x3 bg-dark-bg flex items-center justify-center"
                        style={{ cursor: "pointer" }}
                        onClick={() => setViewer({ url, isVideo: isVid })}
                      >
                        {isVid ? (
                          <video
                            src={url}
                            className="img-fluid"
                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                            playsInline
                          />
                        ) : (
                          <img
                            src={url}
                            alt="post"
                            className="img-fluid"
                            loading="lazy"
                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', objectPosition: 'center' }}
                          />
                        )}
                      </div>
                      <div className="card-body py-2">
                        {/* <div className="small text-gray-400">Timeline</div>
                        <div className="font-semibold truncate">{timelineEvent?.name || 'Post'}</div> */}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}

      <AppFooter buttons={footerButtons} />

      {viewer && (
        <MediaViewer
          src={viewer.url}
          isVideo={viewer.isVideo}
          onClose={() => setViewer(null)}
        />
      )}
    </div>
  );
};

export default PostsList;